/* Evidence checks shared by the UI and regression tests. No prices are manufactured here. */
(function (root) {
  'use strict';
  // First-paint hydration and the controller share one implementation of every price/receipt rule.
  const state = typeof module !== 'undefined' && module.exports ? require('./fs-cart-price-state.js') : root.FsPriceCheckState;
  const {amount,snapshot,proxyPath,hasDiscount,priceReviewEligible,reviewSignature,reviewPrice,privatePolicy,vendorEligible,privateEligible,tierFor,codeApplied,discountTitle,privateSavings,retainedPromotionSavings,itemReviewSignature,reviewReceipt,reviewedKeys,receiptMatches,copyText} = state;
  const MAX_AGE = 15 * 60 * 1000;
  const fingerprint = cart => JSON.stringify(snapshot(cart));
  function fresh(result, now) {
    const checked = Date.parse(result.checkedAt), expires = Date.parse(result.expiresAt);
    return Number.isFinite(checked) && Number.isFinite(expires) && checked <= now + 60000 && checked >= now - MAX_AGE && expires > now && expires > checked && expires <= checked + MAX_AGE;
  }
  function safeUrl(value) {
    try { const u = new URL(value); return u.protocol === 'https:' && !u.username && !u.password ? u.href : null; } catch (_) { return null; }
  }
  function assess(result, item, context, now = Date.now()) {
    const unavailable = {state: 'unavailable'};
    if (!result || result.key !== item.key || String(result.variantId) !== String(item.variant_id)) return unavailable;
    if (result.status === 'no_match') return {state: 'none'};
    if (result.status !== 'compared' || result.currency !== context.currency || result.country !== context.country || result.basis !== 'item_price_excluding_shipping_tax_cart_discounts' || result.quantity !== item.quantity || result.cartLinePrice !== item.final_line_price || result.source !== 'google_shopping') return unavailable;
    if (!fresh(result, now)) return {state: 'expired'};
    // Each offer must describe the same exact variant and sellable condition, not a title-only match.
    if (!Array.isArray(result.offers) || !result.offers.length || result.offers.length > 100) return unavailable;
    const offers = [];
    for (const offer of result.offers) {
      const url = safeUrl(offer.url);
      if (!url || !offer.seller || typeof offer.seller !== 'string' || offer.seller.length > 160 || !amount(offer.unitPrice) || offer.currency !== context.currency || offer.match !== 'exact_variant' || offer.condition !== 'new' || offer.availability !== 'in_stock') return unavailable;
      offers.push({seller: offer.seller, url, unitPrice: offer.unitPrice});
    }
    const unique = new Set(offers.map(o => o.seller.trim().toLowerCase()));
    if (unique.size !== offers.length) return unavailable;
    const lower = offers.some(o => o.unitPrice * item.quantity < item.final_line_price);
    return {state: lower ? 'lower' : 'verified', offers, checkedAt: result.checkedAt, expiresAt: result.expiresAt, currency: context.currency};
  }
  function offerForCart(offer, cart, now = Date.now()) {
    if (!offer || !fresh(offer, now) || offer.cartFingerprint !== fingerprint(cart) || !/^[a-zA-Z0-9_-]{1,80}$/.test(offer.code) || ![20, 25].includes(offer.percent) || !Array.isArray(offer.eligibleKeys) || !offer.eligibleKeys.length) return null;
    if (!offer.eligibleKeys.every(key => cart.items.some(i => i.key === key))) return null;
    return offer;
  }
  function applied(cart, code, beforeTotal) {
    return cart.total_price < beforeTotal && (cart.discount_codes || []).some(d => d.applicable === true && d.code.toLowerCase() === code.toLowerCase());
  }
  function offerApplied(cart, policy) {
    if(policy?.method==='tiered_codes') {
      const titles=policy.tiers.flatMap(t=>[t.title,t.code]).map(discountTitle);
      return cart.items.some(item=>(item.line_level_discount_allocations||[]).some(a=>amount(a.amount) && a.amount>0 && titles.includes(discountTitle(a.discount_application?.title))));
    }
    return !!policy && (policy.method === 'automatic' ? cart.items.some(item=>privateSavings(item,cart,policy)>0) : codeApplied(cart,policy.code));
  }
  function hasOtherDiscount(cart, policy) {
    if(policy?.method==='tiered_codes') {
      // Preserve other codes and let Shopify's native combination rules decide.
      // A conflicting promotion must never be silently removed to force our offer.
      return false;
    }
    // Shopify can retain the old allocation title until the next cart write.
    const own = app => policy?.method === 'automatic' && (discountTitle(app?.title) === discountTitle(policy.title) ||
      (discountTitle(policy.title) === 'price match' && discountTitle(app?.title) === 'neurowave private price'));
    return (cart.discount_codes || []).some(d=>d.applicable) ||
      (cart.cart_level_discount_applications || []).some(app=>!own(app)) ||
      cart.items.some(item=>(item.line_level_discount_allocations || []).some(a=>!own(a.discount_application)));
  }
  function lineIdentity(item) {
    return JSON.stringify([String(item.variant_id),item.quantity,item.original_price,
      Object.entries(item.properties || {}).sort(([a],[b])=>a.localeCompare(b)),
      item.selling_plan_allocation?.selling_plan?.id || null]);
  }
  function cartContents(cart, property) {
    const groups = new Map();
    for (const item of cart.items) {
      const properties = Object.entries(item.properties || {}).filter(([key])=>key!==property).sort(([a],[b])=>a.localeCompare(b));
      const identity = JSON.stringify([String(item.variant_id),item.original_price,properties,item.selling_plan_allocation?.selling_plan?.id || null]);
      groups.set(identity,(groups.get(identity)||0)+item.quantity);
    }
    return JSON.stringify([cart.currency,[...groups].sort(([a],[b])=>a.localeCompare(b))]);
  }
  function variantReview(cart, facts, policy) {
    if (reviewSignature(cart)!==reviewSignature(facts)) throw Error('Stale review');
    const items=new Map();
    for(const item of cart.items) {
      if(!priceReviewEligible(item)) continue;
      const fact=facts.items.find(f=>f.key===item.key);
      if(policy?.method==='tiered_codes' && !Array.isArray(fact?.collection_ids)) throw Error('Missing collection facts');
      if(!privateEligible(item,fact,policy)) continue;
      const p=fact.catalog_line_price/item.quantity, id=String(item.variant_id);
      if(!Number.isSafeInteger(p)||p<=0) throw Error('Invalid unit price');
      const tier=policy?.method==='tiered_codes'?tierFor(item,fact,policy):null;
      items.set(id+':'+p,{id,p,...(tier?{t:tier.percent}:{})});
    }
    if(items.size>250) throw Error('Review limit exceeded');
    return JSON.stringify({v:policy?.method==='tiered_codes'?4:3,c:cart.currency,...(policy?.method==='tiered_codes'?{r:policy.revision}:{}),items:[...items.values()].sort((a,b)=>a.id.localeCompare(b.id)||a.p-b.p)});
  }
  function codePlan(cart,facts,policy) {
    if(policy?.method!=='tiered_codes') return null;
    if(reviewSignature(cart)!==reviewSignature(facts)) throw Error('Stale review');
    const own=new Set(policy.tiers.map(t=>discountTitle(t.code)));
    const required=[...new Set(cart.items.map(item=>tierFor(item,facts.items.find(f=>f.key===item.key),policy)?.code).filter(Boolean))];
    const codes=[], seen=new Set();
    for(const code of [...(cart.discount_codes||[]).filter(d=>!own.has(discountTitle(d.code))).map(d=>d.code),...required]) {
      const value=String(code), normalized=discountTitle(value);
      if(!normalized || seen.has(normalized))continue;
      seen.add(normalized);codes.push(value);
    }
    // Shopify accepts at most five product/order discount codes per cart.
    // Never discard a customer's existing code just to fit our tier codes.
    return {codes,required,discount:codes.join(','),conflict:codes.length>5};
  }
  function offerMissing(cart,facts,policy) {
    return !!policy && cart.items.some(item=>{
      const fact=facts.items.find(f=>f.key===item.key);
      if(!privateEligible(item,fact,policy)) return false;
      const percent=policy.method==='tiered_codes'?tierFor(item,fact,policy).percent:policy.percent;
      const expected=Math.floor(fact.catalog_line_price/item.quantity*percent/100)*item.quantity;
      const savings=Math.max(privateSavings(item,cart,policy,fact),retainedPromotionSavings(item,cart,policy,fact));
      return savings<=0 || savings<expected;
    });
  }
  function legacyMerge(cart) {
    const groups=new Map(), updates={};
    for(const item of cart.items) {
      const properties=Object.entries(item.properties||{}).filter(([key])=>key!=='_nw_reviewed_item').sort(([a],[b])=>a.localeCompare(b));
      const id=JSON.stringify([String(item.variant_id),item.original_price,properties,item.selling_plan_allocation?.selling_plan?.id||null]);
      if(!groups.has(id))groups.set(id,[]);
      groups.get(id).push(item);
    }
    for(const items of groups.values()) {
      const clean=items.find(item=>!Object.prototype.hasOwnProperty.call(item.properties||{},'_nw_reviewed_item'));
      const legacy=items.filter(item=>Object.prototype.hasOwnProperty.call(item.properties||{},'_nw_reviewed_item'));
      if(!clean || !legacy.length)continue;
      const quantity=clean.quantity+legacy.reduce((sum,item)=>sum+item.quantity,0);
      if(!Number.isSafeInteger(quantity)||quantity<1)throw Error('Invalid merged quantity');
      updates[clean.key]=quantity;
      for(const item of legacy)updates[item.key]=0;
    }
    return Object.keys(updates).length?updates:null;
  }

  function reviewDuration(itemCount) {
    const count=Number.isFinite(itemCount)?Math.max(1,Math.floor(itemCount)):1;
    return Math.min(5000,1200+(count-1)*1000);
  }
  function nextMotion(previous, random = Math.random) {
    const valid = n => Number.isInteger(n) && n >= 0 && n < 20;
    const last = valid(previous?.last) ? previous.last : null;
    let remaining = previous?.version === 1 && Array.isArray(previous.remaining) && previous.remaining.length < 20 &&
      previous.remaining.every(n => valid(n) && n !== last) && new Set(previous.remaining).size === previous.remaining.length ? [...previous.remaining] : [];
    if (!remaining.length) {
      remaining=Array.from({length:20},(_,i)=>i);
      for (let i=19;i>0;i--) { const j=Math.floor(random()*(i+1)); [remaining[i],remaining[j]]=[remaining[j],remaining[i]]; }
      if (remaining[0] === last) [remaining[0],remaining[1]]=[remaining[1],remaining[0]];
    }
    return {version:1,last:remaining.shift(),remaining};
  }
  function motionPattern(id) {
    id=Number.isInteger(id) && id>=0 && id<20 ? id : 0;
    const family=Math.floor(id/4), order=[[0,1,2,3,4,5,6,7],[7,6,5,4,3,2,1,0],[0,4,2,6,1,5,3,7],[0,2,4,6,1,3,5,7]][id%4];
    const point=(r,a)=>[Math.round(Math.max(-32,Math.min(16,Math.cos(a)*r))*10)/10,Math.round(Math.max(-32,Math.min(32,Math.sin(a)*r))*10)/10];
    const pair=p=>p.join(' ');
    return Array.from({length:8},(_,i)=>{
      const angle=-Math.PI/2+i*Math.PI/4, bend=((i+id)%2 ? 1 : -1)*(.13+family*.025);
      const start=point(9,angle),middle=point(16+(id+i)%5,angle+bend),end=point(24+(id*7+i*3)%9,angle+((id+i)%3-1)*.07);
      const control=point(23,angle-bend), tail=point(27,angle+bend*.75);
      let d='M'+pair(start);
      if (family===0) d+='L'+pair(middle)+' '+pair(end);
      else if (family===1) d+='Q'+pair(middle)+' '+pair(end);
      else if (family===2) d+='L'+pair(middle)+' '+pair(end)+(i%2===0?'M'+pair(middle)+'L'+pair(tail):'');
      else if (family===3) d+='L'+pair(middle)+'Q'+pair(control)+' '+pair(end);
      else d+='C'+pair(middle)+' '+pair(control)+' '+pair(end);
      return {d,nodes:family===2 && i%2===0 ? [end,tail] : i%3===0 ? [middle,end] : [end],
        delay:order.indexOf(i)*.055+(id%3)*.025,duration:1.05+((id+i)%4)*.15,glow:2.5+((id+i)%3)*.25,signal:(id+i)%3===0};
    });
  }
  const api = {legacyMerge,variantReview,codePlan,offerMissing,tierFor,reviewedKeys,priceReviewEligible,hasOtherDiscount,lineIdentity,cartContents,nextMotion,motionPattern,copyText,reviewDuration,reviewReceipt,receiptMatches,offerApplied,snapshot, fingerprint, proxyPath, fresh, safeUrl, assess, offerForCart, hasDiscount, applied, reviewSignature, reviewPrice, privatePolicy, privateEligible, codeApplied, privateSavings,retainedPromotionSavings,vendorEligible};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.FsPriceCheckCore = api;
})(typeof window !== 'undefined' ? window : this);
